import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';
import NumberField from '../NumberField/NumberField';
import Select from '../Select/Select';
import TextField from '../TextField/TextField';
import AppCheckbox from '../Checkbox/Checkbox';
import { JsonEditor } from '..';

type InputProps =
  | AppTextFieldProps
  | AppNumberFieldProps
  | AppSelectProps
  | AppJsonEditorProps;

export type AppControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = InputProps & {
  variant: ControllerVariant;
  control?: Control<TFieldValues>;
  name: TName;
  rules?: RegisterOptions<TFieldValues>;
  enableTrim?: boolean;
};

const AppController = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  variant,
  control,
  name,
  rules,
  enableTrim,
  ...inputProps
}: AppControllerProps<TFieldValues, TName>) => (
  <Controller
    control={control}
    name={name}
    rules={rules}
    render={({ field, fieldState }) => {
      switch (variant) {
        case 'TextField':
          return (
            <TextField
              id={name}
              {...(inputProps as AppTextFieldProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
              enableTrim={enableTrim}
            />
          );
        case 'NumberField':
          return (
            <NumberField
              id={name}
              {...(inputProps as AppNumberFieldProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
        case 'Select':
          return (
            <Select
              id={name}
              {...(inputProps as AppSelectProps)}
              required={!!rules?.required}
              value={field.value}
              error={fieldState.error}
              onChange={field.onChange}
            />
          );
        case 'CheckBox':
          return (
            <AppCheckbox
              {...(inputProps as CheckBoxProps)}
              checked={field.value}
              onChange={field.onChange}
            />
          );
        case 'JsonEditor':
          return (
            <JsonEditor
              id={name}
              {...(inputProps as AppJsonEditorProps)}
              required={!!rules?.required}
              value={field.value}
              onChange={field.onChange}
            />
          );
      }
    }}
  />
);

export default AppController;
